import { slot } from "./dom.js";
import { wireExamples } from "./examples.js";
import { numberRows } from "./number-labels.js";
import { readingRow } from "./number-rows.js";
import {
  type NumberReadings,
  type NumeralReading,
  readFraction,
  readNumber,
} from "./numbers.js";
import { wireShareLink } from "./share-link.js";
import { readUrlState, rememberUrl } from "./url-state.js";

/**
 * The Chinese numbers tool.
 *
 * A number in, and every way of saying it out. No dictionary, no settings: a
 * number has no context to disambiguate, which is exactly why the page shows
 * the alternatives rather than picking one.
 */

/**
 * Start the numbers tool.
 */
export function startNumbersTool(container: HTMLElement): void {
  const input = slot(container, "input");
  const output = slot(container, "output");

  if (!(input instanceof HTMLInputElement) || output === undefined) {
    return;
  }

  const fractionTop = slot(container, "fraction-numerator");
  const fractionBottom = slot(container, "fraction-denominator");
  const fractionOutput = slot(container, "fraction-output");

  const show = (): void => {
    rememberUrl({ text: input.value });
    renderReadings(output, readNumber(input.value));

    if (
      fractionTop instanceof HTMLInputElement &&
      fractionBottom instanceof HTMLInputElement &&
      fractionOutput !== undefined
    ) {
      renderFraction(
        fractionOutput,
        readFraction(fractionTop.value, fractionBottom.value),
      );
    }
  };

  const fromUrl = readUrlState(location.search);
  if (fromUrl.text !== undefined) {
    input.value = fromUrl.text;
  }

  wireShareLink(container, () => ({ text: input.value }));

  input.addEventListener("input", show);
  fractionTop?.addEventListener("input", show);
  fractionBottom?.addEventListener("input", show);

  wireExamples(container, (text) => {
    input.value = text;
    show();
  });

  show();
}

/**
 * Show every reading a number has.
 *
 * An empty input shows nothing rather than an error: the page opens on one.
 */
function renderReadings(output: Element, found: NumberReadings): void {
  output.replaceChildren(
    ...numberRows
      .map((row) => {
        const reading = found[row.key];

        return reading === undefined
          ? undefined
          : readingRow(row.label, row.note, reading);
      })
      .filter((row) => row !== undefined),
  );
}

/**
 * Show the fraction, where the two fields hold one.
 */
function renderFraction(
  output: Element,
  reading: NumeralReading | undefined,
): void {
  output.replaceChildren(
    ...(reading === undefined
      ? []
      : [
          readingRow(
            "As a fraction",
            "The denominator is said first.",
            reading,
          ),
        ]),
  );
}
